import React from 'react';
import FieldRow from './FolioFieldRow';
import {
    Checkbox,
    ComboBox,
    DefaultButton,
    Dropdown,
    getTheme,
    IconButton,
    IDropdownOption,
    Link as FluentLink,
    Pivot,
    PivotItem,
    Separator,
    Stack,
    Text,
    TextField
} from '@fluentui/react';
import { IExtensionFolio, IHelpLink, IImportValueMap, ISubExtension } from '../../models/Extension';
import FolioFieldList, { newField } from './FolioFieldList';
import FolioQuoteLinkEditor from './FolioQuoteLinkEditor';
import AccordionBox from '../../components/Common/AccordionBox';
import { FOLIO_EDITOR_STRINGS as S } from './folioEditorStrings';
import { useGetFolioEntitlementSuggestionsQuery } from '../../store/endpoints/FolioAdminEndpoints';
import { PRODUCT_LEVELS } from '../../Constants';

// The structured folio editor.
//
// A folio is a nested document, so the form is organised the way the thing itself works rather than the
// way the JSON is shaped: what it is, the settings every capability reads, and then one section per
// capability with its own overrides and its own documentation. The URL fields that only matter for
// ordering stay collapsed, because most folios never set them and an author reading a distributor's docs
// should not have to scroll past eight empty endpoint boxes to reach the credential list.

const theme = getTheme();

const PRODUCT_LEVEL_OPTIONS: IDropdownOption[] = PRODUCT_LEVELS.map(level => ({ key: level, text: level }));

const CATEGORIES: IDropdownOption[] = [
    { key: 'import', text: 'Import — pulls a catalog or file on a schedule' },
    { key: 'realtime', text: 'Real time — live pricing and availability' },
    { key: 'procurement', text: 'Procurement — places orders' },
    { key: 'export', text: 'Export — sends data out' },
    { key: 'customerImport', text: 'Customer import' },
    { key: 'directImport', text: 'Direct import' },
    { key: 'tax', text: 'Tax' },
    { key: 'locations', text: 'Locations' },
];

/** The ordering endpoints, which live on the folio rather than per capability. */
const ORDER_URL_FIELDS: { key: keyof IExtensionFolio, label: string }[] = [
    { key: 'orderRequirementsUrl', label: 'Order requirements URL' },
    { key: 'itemRequirementsUrl', label: 'Item requirements URL' },
    { key: 'freightOptionsUrl', label: 'Freight options URL' },
    { key: 'carrierOptionsUrl', label: 'Carrier options URL' },
    { key: 'warehouseOptionsUrl', label: 'Warehouse options URL' },
    { key: 'orderUpdateUrl', label: 'Order update URL' },
];

/** The small grey line a TextField gets from `description`, for controls that have no such slot. */
const FieldHelp: React.FC<{ text: string }> = ({ text }) => (
    <Text variant='small' style={{ color: theme.palette.neutralSecondary, marginTop: '-0.35em' }}>{text}</Text>
);

/**
 * One tab's contents. No heading of its own: the tab is the heading, and repeating it under the tab strip
 * was the first thing every tab said. The help line stays — it is the part that is not already on screen.
 */
const TabPanel: React.FC<{ help?: string, children: React.ReactNode }> = ({ help, children }) => (
    <Stack tokens={{ childrenGap: '0.5em' }}>
        {help && <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>{help}</Text>}
        {children}
    </Stack>
);

const Section: React.FC<{ title: string, help?: string, children: React.ReactNode }> = ({ title, help, children }) => (
    <Stack tokens={{ childrenGap: '0.5em' }}>
        <Separator alignContent='start'><span style={{ fontWeight: 600 }}>{title}</span></Separator>
        {help && (
            <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>{help}</Text>
        )}
        {children}
    </Stack>
);

const HelpLinkList: React.FC<{
    links: IHelpLink[],
    onChange: (links: IHelpLink[]) => void
}> = ({ links, onChange }) => (
    <Stack tokens={{ childrenGap: '0.4em' }}>
        {links.map((link, index) => (
            <FieldRow key={index} align='controls'>
                <TextField
                    label={index === 0 ? S.helpLinkText : undefined}
                    value={link.text ?? ''}
                    onChange={(_e, v) => onChange(links.map((l, i) => i === index ? { ...l, text: v ?? '' } : l))}
                    styles={{ root: { flexGrow: 1 } }}
                />
                <TextField
                    label={index === 0 ? S.helpLinkUrl : undefined}
                    value={link.url ?? ''}
                    onChange={(_e, v) => onChange(links.map((l, i) => i === index ? { ...l, url: v ?? '' } : l))}
                    styles={{ root: { flexGrow: 2 } }}
                />
                <IconButton
                    iconProps={{ iconName: 'Delete' }}
                    title={S.removeHelpLink}
                    ariaLabel={S.removeHelpLink}
                    onClick={() => onChange(links.filter((_l, i) => i !== index))}
                />
            </FieldRow>
        ))}
        <Stack horizontal>
            <DefaultButton
                iconProps={{ iconName: 'Add' }}
                text={S.addHelpLink}
                onClick={() => onChange([...links, { text: '', url: '' }])}
            />
        </Stack>
    </Stack>
);

// The import fields a distributor most often codes its own way. Free text covers the rest.
const VALUE_MAP_FIELDS: IDropdownOption[] = [
    { key: 'inStock', text: 'inStock' },
    { key: 'active', text: 'active' },
    { key: 'status', text: 'status' },
    { key: 'featured', text: 'featured' },
    { key: 'manufacturer', text: 'manufacturer' },
    { key: 'costMessage', text: 'costMessage' },
];

const ValueMapList: React.FC<{
    maps: IImportValueMap[],
    onChange: (maps: IImportValueMap[]) => void
}> = ({ maps, onChange }) => {
    const update = (index: number, change: Partial<IImportValueMap>) =>
        onChange(maps.map((m, i) => i === index ? { ...m, ...change } : m));
    return (
        <Stack tokens={{ childrenGap: '0.4em' }}>
            {maps.map((map, index) => (
                <FieldRow key={index} align='controls'>
                    <ComboBox
                        label={index === 0 ? S.valueMapField : undefined}
                        options={VALUE_MAP_FIELDS}
                        allowFreeform
                        autoComplete='on'
                        text={map.field}
                        onChange={(_e, option, _i, value) => update(index, { field: String(option?.key ?? value ?? '') })}
                        styles={{ root: { minWidth: 160 } }}
                    />
                    <TextField
                        label={index === 0 ? S.valueMapFrom : undefined}
                        value={map.from}
                        onChange={(_e, v) => update(index, { from: v ?? '' })}
                        styles={{ root: { flexGrow: 1 } }}
                    />
                    <TextField
                        label={index === 0 ? S.valueMapTo : undefined}
                        value={map.to}
                        onChange={(_e, v) => update(index, { to: v ?? '' })}
                        styles={{ root: { flexGrow: 1 } }}
                    />
                    <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title={S.removeValueMap}
                        ariaLabel={S.removeValueMap}
                        onClick={() => onChange(maps.filter((_m, i) => i !== index))}
                    />
                </FieldRow>
            ))}
            <Stack horizontal>
                <DefaultButton
                    iconProps={{ iconName: 'Add' }}
                    text={S.addValueMap}
                    onClick={() => onChange([...maps, { field: maps[maps.length - 1]?.field ?? 'inStock', from: '', to: '' }])}
                />
            </Stack>
        </Stack>
    );
};

const CapabilityEditor: React.FC<{
    sub: ISubExtension,
    sharedFieldNames: string[],
    isDefault: boolean,
    onChange: (sub: ISubExtension) => void,
    onToggleDefault: (on: boolean) => void,
    onRemove: () => void,
}> = ({ sub, sharedFieldNames, isDefault, onChange, onToggleDefault, onRemove }) => {
    const [expanded, setExpanded] = React.useState(false);

    return (
        <AccordionBox
            title={sub.name || sub.identifier || S.untitledCapability}
            boldTitle
            isExpanded={expanded}
            onToggle={setExpanded}
            secondaryTitle={
                // The header's whole surface toggles the box, so the two controls that live up here stop
                // the click from reaching it. They are in the header because they are what an author does
                // to a capability without opening it: turn it on by default, or take it out.
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '0.75em', marginLeft: '0.75em' }}>
                    <Text variant='small' style={{ color: theme.palette.neutralSecondary }}>{sub.category}</Text>
                    <span onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                            label={S.onByDefault}
                            checked={isDefault}
                            onChange={(_e, checked) => onToggleDefault(!!checked)}
                        />
                    </span>
                    <IconButton
                        iconProps={{ iconName: 'Delete' }}
                        title={S.removeCapability}
                        ariaLabel={S.removeCapability}
                        styles={{ root: { height: 24 } }}
                        onClick={(e) => { e.stopPropagation(); onRemove(); }}
                    />
                </span>
            }
        >
            <Stack tokens={{ childrenGap: '0.75em' }}>
                <FieldRow>
                    <TextField
                        label={S.capabilityIdentifier}
                        value={sub.identifier}
                        onChange={(_e, v) => onChange({ ...sub, identifier: v ?? '' })}
                        description={S.capabilityIdentifierHelp}
                        styles={{ root: { flexGrow: 1, minWidth: 200, maxWidth: 340 } }}
                    />
                    <TextField
                        label={S.capabilityName}
                        value={sub.name ?? ''}
                        onChange={(_e, v) => onChange({ ...sub, name: v ?? '' })}
                        styles={{ root: { flexGrow: 1, minWidth: 200, maxWidth: 340 } }}
                    />
                </FieldRow>

                <TextField
                    label={S.capabilityDescription}
                    multiline
                    rows={2}
                    value={sub.description ?? ''}
                    onChange={(_e, v) => onChange({ ...sub, description: v ?? '' })}
                />

                <FieldRow>
                    <Dropdown
                        label={S.capabilityCategory}
                        selectedKey={sub.category}
                        options={CATEGORIES}
                        onChange={(_e, o) => onChange({
                            ...sub,
                            category: String(o?.key ?? ''),
                            // extensionType tracks category unless the author has set it apart.
                            extensionType: sub.extensionType && sub.extensionType !== sub.category
                                ? sub.extensionType
                                : String(o?.key ?? ''),
                        })}
                        styles={{ root: { flexGrow: 1, minWidth: 200, maxWidth: 340 } }}
                    />
                    <TextField
                        label={S.capabilitySubCategory}
                        value={sub.subCategory ?? ''}
                        onChange={(_e, v) => onChange({ ...sub, subCategory: v ?? '' })}
                        description={S.capabilitySubCategoryHelp}
                        styles={{ root: { flexGrow: 1, minWidth: 200, maxWidth: 340 } }}
                    />
                </FieldRow>

                <TextField
                    label={S.capabilityUrl}
                    value={sub.url ?? ''}
                    onChange={(_e, v) => onChange({ ...sub, url: v ?? '' })}
                    description={S.capabilityUrlHelp}
                />

                <Section title={S.capabilityFields} help={S.capabilityFieldsHelp}>
                    <FolioFieldList
                        fields={sub.additionalData ?? []}
                        shadowable={sharedFieldNames}
                        onChange={(fields) => onChange({ ...sub, additionalData: fields })}
                    />
                </Section>

                <Section title={S.capabilityHelpLinks} help={S.capabilityHelpLinksHelp}>
                    <HelpLinkList
                        links={sub.helpLinks ?? []}
                        onChange={(links) => onChange({ ...sub, helpLinks: links })}
                    />
                </Section>

                {/* Only the importer reads these. Still shown elsewhere when present, so they can't hide. */}
                {(sub.category === 'import' || !!sub.valueMaps?.length) &&
                    <Section title={S.capabilityValueMaps} help={S.capabilityValueMapsHelp}>
                        <ValueMapList
                            maps={sub.valueMaps ?? []}
                            onChange={(maps) => onChange({ ...sub, valueMaps: maps.length ? maps : undefined })}
                        />
                    </Section>}
            </Stack>
        </AccordionBox>
    );
};

export interface IFolioFormProps {
    folio: IExtensionFolio;
    onChange: (folio: IExtensionFolio) => void;
    /** Global admins can widen a folio's reach; authors cannot, and the server enforces that. */
    isGlobalAdmin: boolean;
}

const FolioForm: React.FC<IFolioFormProps> = ({ folio, onChange, isGlobalAdmin }) => {
    // A folio is six unrelated subjects in one document — what it is, what it can do, what it asks for,
    // where it posts, what to read, who gets it. As one scroll, reaching any of them meant knowing how far
    // down it lived, and the capability list, which is the long one, buried everything after it.
    const [tab, setTab] = React.useState<string>('basics');
    const [showOrderUrls, setShowOrderUrls] = React.useState(
        () => ORDER_URL_FIELDS.some(f => !!folio[f.key])
    );

    const subs = folio.subExtensions ?? {};
    const sharedFieldNames = (folio.sharedFields ?? []).map(f => f.field);
    const defaults = folio.defaultSubExtensions ?? [];

    /** Renaming an identifier has to move the map key with it, or the two disagree and nothing installs. */
    const replaceSub = (key: string, next: ISubExtension) => {
        const rekeyed: { [k: string]: ISubExtension } = {};
        for (const [existingKey, existingSub] of Object.entries(subs)) {
            if (existingKey === key) rekeyed[next.identifier || existingKey] = next;
            else rekeyed[existingKey] = existingSub;
        }
        const renamed = key !== next.identifier && next.identifier;
        onChange({
            ...folio,
            subExtensions: rekeyed,
            defaultSubExtensions: renamed
                ? defaults.map(d => (d === key ? next.identifier : d))
                : defaults,
        });
    };

    const removeSub = (key: string) => {
        const remaining = { ...subs };
        delete remaining[key];
        onChange({
            ...folio,
            subExtensions: remaining,
            defaultSubExtensions: defaults.filter(d => d !== key),
        });
    };

    const addSub = () => {
        // A stable placeholder key, numbered so adding two in a row does not collide.
        let index = Object.keys(subs).length + 1;
        while (subs[`capability${index}`]) index++;
        const identifier = `capability${index}`;
        onChange({
            ...folio,
            subExtensions: {
                ...subs,
                [identifier]: {
                    identifier,
                    name: '',
                    category: 'import',
                    extensionType: 'import',
                    url: '',
                    allowFileLibrary: false,
                    additionalData: [newField()],
                },
            },
        });
    };

    const toggleDefault = (identifier: string, on: boolean) => onChange({
        ...folio,
        defaultSubExtensions: on
            ? [...defaults, identifier]
            : defaults.filter(d => d !== identifier),
    });

    // Organizations stay free text: an org id is an arbitrary tenant key, and there is no list of them
    // this screen could offer that would not be longer than the field.
    const setList = (key: 'organizations', value: string) => onChange({
        ...folio,
        [key]: value.split(',').map(v => v.trim()).filter(Boolean),
    });

    // Every entitlement tag in use as a gate anywhere on the platform — the extension catalog, other
    // folios, this organization's own list — plus whatever this folio already carries, so a tag that is
    // set but no longer used elsewhere still shows as selected rather than silently vanishing.
    const suggestions = useGetFolioEntitlementSuggestionsQuery(undefined, { skip: !isGlobalAdmin });
    const [newEntitlement, setNewEntitlement] = React.useState('');
    const entitlementOptions: IDropdownOption[] = React.useMemo(() => {
        const seen = new Set<string>();
        for (const tag of suggestions.data ?? []) if (tag.trim()) seen.add(tag.trim());
        for (const tag of folio.entitlements ?? []) if (tag.trim()) seen.add(tag.trim());
        return Array.from(seen).sort().map(tag => ({ key: tag, text: tag }));
    }, [suggestions.data, folio.entitlements]);

    const addEntitlement = (tag: string) => {
        const value = tag.trim();
        if (!value) return;
        const current = folio.entitlements ?? [];
        if (!current.includes(value)) onChange({ ...folio, entitlements: [...current, value] });
        setNewEntitlement('');
    };

    return (
        <Pivot
            selectedKey={tab}
            onLinkClick={(item) => setTab(item?.props.itemKey ?? 'basics')}
        >
                <PivotItem headerText={S.tabBasics} itemKey='basics'>
                    <div style={{ paddingTop: '1em' }}>
                    <TabPanel>
                        {/* Name and logo are not here: they identify the folio, so they live in the
                            panel header where they are visible from every tab. */}
                        <TextField
                            label={S.folioDescription}
                            multiline
                            rows={2}
                            value={folio.description ?? ''}
                            onChange={(_e, v) => onChange({ ...folio, description: v ?? '' })}
                        />
                        <TextField
                            label={S.folioVendor}
                            value={folio.defaultVendor ?? ''}
                            onChange={(_e, v) => onChange({ ...folio, defaultVendor: v ?? '' })}
                            description={S.folioVendorHelp}
                            styles={{ root: { maxWidth: 320 } }}
                        />

                        {/* Two short lists that were a tab each. Neither fills a screen, and both are
                            about the folio as a whole rather than about anything it does. */}
                        <Section title={S.tabDocumentation} help={S.sectionHelpLinksHelp}>
                        <HelpLinkList
                            links={folio.helpLinks ?? []}
                            onChange={(links) => onChange({ ...folio, helpLinks: links })}
                        />
                        </Section>

                        {isGlobalAdmin && (
                        <Section title={S.tabAudience} help={S.sectionAudienceHelp}>
                        <TextField
                            label={S.folioOrganizations}
                            value={(folio.organizations ?? []).join(', ')}
                            onChange={(_e, v) => setList('organizations', v ?? '')}
                            description={S.folioOrganizationsHelp}
                        />
                        {/* Two controls rather than one, because Fluent has no reliable single one. A
                            multiSelect ComboBox with allowFreeform stops accepting input once a typed value
                            is in play, which is what this was; a Dropdown cannot be typed into at all. So:
                            pick from what exists, or add a tag that does not exist yet. */}
                        <Dropdown
                            label={S.folioEntitlements}
                            multiSelect
                            options={entitlementOptions}
                            selectedKeys={folio.entitlements ?? []}
                            placeholder={suggestions.isLoading
                                ? S.folioEntitlementsLoading
                                : S.folioEntitlementsPlaceholder}
                            onChange={(_e, option) => {
                                if (!option) return;
                                const key = String(option.key);
                                const current = folio.entitlements ?? [];
                                onChange({
                                    ...folio,
                                    entitlements: option.selected
                                        ? [...current, key]
                                        : current.filter(e => e !== key),
                                });
                            }}
                        />
                        <FieldRow align='controls'>
                            <TextField
                                label={S.folioEntitlementAdd}
                                value={newEntitlement}
                                onChange={(_e, v) => setNewEntitlement(v ?? '')}
                                onKeyDown={(e) => {
                                    if (e.key !== 'Enter') return;
                                    // Otherwise the panel takes it as a submit and saves the folio.
                                    e.preventDefault();
                                    addEntitlement(newEntitlement);
                                }}
                                placeholder={S.folioEntitlementAddPlaceholder}
                                styles={{ root: { flexGrow: 1, maxWidth: 320 } }}
                            />
                            <DefaultButton
                                text={S.add}
                                disabled={!newEntitlement.trim()}
                                onClick={() => addEntitlement(newEntitlement)}
                            />
                        </FieldRow>
                        <FieldHelp text={S.folioEntitlementsHelp} />
                        <Dropdown
                            label={S.folioProductLevel}
                            multiSelect
                            options={PRODUCT_LEVEL_OPTIONS}
                            selectedKeys={folio.productLevel ?? []}
                            placeholder={S.folioProductLevelPlaceholder}
                            onChange={(_e, option) => {
                                if (!option) return;
                                const key = String(option.key);
                                const current = folio.productLevel ?? [];
                                onChange({
                                    ...folio,
                                    productLevel: option.selected
                                        ? [...current, key]
                                        : current.filter(l => l !== key),
                                });
                            }}
                        />
                        <FieldHelp text={S.folioProductLevelHelp} />
                        </Section>
                        )}
                    </TabPanel>
                    </div>
                </PivotItem>
                <PivotItem headerText={S.tabCapabilities} itemKey='capabilities'>
                    <div style={{ paddingTop: '1em' }}>
                    <TabPanel help={S.sectionCapabilitiesHelp}>
                        <Stack tokens={{ childrenGap: '0.5em' }}>
                            {Object.entries(subs).map(([key, sub]) => (
                                <CapabilityEditor
                                    key={key}
                                    sub={sub}
                                    sharedFieldNames={sharedFieldNames}
                                    isDefault={defaults.includes(key)}
                                    onChange={(next) => replaceSub(key, next)}
                                    onToggleDefault={(on) => toggleDefault(key, on)}
                                    onRemove={() => removeSub(key)}
                                />
                            ))}
                            <Stack horizontal>
                                <DefaultButton iconProps={{ iconName: 'Add' }} text={S.addCapability} onClick={addSub} />
                            </Stack>
                        </Stack>
                    </TabPanel>
                    </div>
                </PivotItem>
                <PivotItem headerText={S.tabQuoteLink} itemKey='quoteLink'>
                    <div style={{ paddingTop: '1em' }}>
                    <TabPanel help={S.sectionQuoteLinkHelp}>
                        <FolioQuoteLinkEditor folio={folio} onChange={onChange} />
                    </TabPanel>
                    </div>
                </PivotItem>
                <PivotItem headerText={S.tabShared} itemKey='shared'>
                    <div style={{ paddingTop: '1em' }}>
                    <TabPanel help={S.sectionSharedHelp}>
                        <FolioFieldList
                            fields={folio.sharedFields ?? []}
                            onChange={(fields) => onChange({ ...folio, sharedFields: fields })}
                        />
                    </TabPanel>
                    </div>
                </PivotItem>
                <PivotItem headerText={S.tabEndpoints} itemKey='endpoints'>
                    <div style={{ paddingTop: '1em' }}>
                    <TabPanel help={S.sectionEndpointsHelp}>
                        <TextField
                            label={S.folioBaseUrl}
                            value={folio.baseUrl ?? ''}
                            onChange={(_e, v) => onChange({ ...folio, baseUrl: v ?? '' })}
                            description={S.folioBaseUrlHelp}
                            placeholder='https://connector.vendor.com'
                        />
                        <TextField
                            label={S.folioEncryption}
                            value={folio.encryption ?? ''}
                            onChange={(_e, v) => onChange({ ...folio, encryption: v ?? '' })}
                        />
                        <TextField
                            label={S.folioValidate}
                            value={folio.validateUrl ?? ''}
                            onChange={(_e, v) => onChange({ ...folio, validateUrl: v ?? '' })}
                            description={S.folioValidateHelp}
                        />
                        <FluentLink onClick={() => setShowOrderUrls(!showOrderUrls)}>
                            {showOrderUrls ? S.hideOrderUrls : S.showOrderUrls}
                        </FluentLink>
                        {showOrderUrls && ORDER_URL_FIELDS.map(({ key, label }) => (
                            <TextField
                                key={key}
                                label={label}
                                value={(folio[key] as string | undefined) ?? ''}
                                onChange={(_e, v) => onChange({ ...folio, [key]: v ?? '' })}
                            />
                        ))}
                    </TabPanel>
                    </div>
                </PivotItem>
        </Pivot>
    );
};

export default FolioForm;
